import { Skeleton } from "@universe/ui/components/skeleton";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";

import { BLOCKCHAIN_MAPPING } from "@/constants";

type BlockchainNetworkIconProps = {
  network: string;
  size?: string;
  className?: string;
  flatColor?: boolean;
};

export const getBlockchainIconPath = (network: string) => {
  const blockchain =
    BLOCKCHAIN_MAPPING[network as keyof typeof BLOCKCHAIN_MAPPING];

  if (!blockchain) {
    return null;
  }

  return `/icons/crypto-png/${blockchain.icon}`;
};

const BlockchainNetworkIcon = ({
  network,
  size = "24px",
  className = "",
  flatColor = false,
}: BlockchainNetworkIconProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const iconPath = getBlockchainIconPath(network);

  const sizeNumber = Number(size.replace("px", ""));

  // Show fallback if no valid icon path or image error
  if (!iconPath || imageError) {
    if (!network) return null;
    return (
      <div style={{ width: size, height: size }}>
        <div
          className="bg-brand-strong flex items-center justify-center text-white text-xs font-semibold rounded-full"
          style={{ width: size, height: size }}
        >
          {network.slice(0, 3).toUpperCase()}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size }}
    >
      <AnimatePresence>
        {!imageLoaded && (
          <motion.div
            key="skeleton"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0"
          >
            <Skeleton className="h-full w-full rounded-full" />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: imageLoaded ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0"
      >
        <Image
          alt={`${network} icon`}
          className="rounded-full block"
          src={iconPath}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          width={sizeNumber}
          height={sizeNumber}
          unoptimized
        />
        {!flatColor && (
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: `
              radial-gradient(
                ellipse at 30% 30%,
                rgba(255, 255, 255, 0.2) 0%,
                rgba(255, 255, 255, 0.2) 30%,
                transparent 60%
              ),
              linear-gradient(
                135deg,
                transparent 40%,
                rgba(255, 255, 255, 0.1) 50%,
                transparent 60%
              )
            `,
              boxShadow:
                "inset 0 1px 2px rgba(255, 255, 255, 0.6), inset 0 -1px 2px rgba(0, 0, 0, 0.1)",
            }}
          />
        )}
      </motion.div>
    </div>
  );
};

export default BlockchainNetworkIcon;
